// src/auth/PermissionsDriftNotice.jsx
//
// The affordance for a permissions drift (see permissionsDrift.js): the local
// claims and the server disagree, and the only honest fix is a new token.
//
// ⚠️ Two rules this file exists to enforce:
//   1. ONE string for both directions — a revoked right refused by the server
//      and a granted right that will not appear are the same problem to the
//      person reading it, and the same remedy.
//   2. The action is NOT implemented here. It is `reauthenticate` from
//      AuthContext, passed in — so what the remedy DOES can change without this
//      file moving. Today it signs the user out: the backend mints the claims at
//      POST /auth/login and serves no route that re-mints them, so a fresh
//      sign-in is the fix. Keep the indirection for whatever replaces it next.
//
// ⛔ Do not add a "refresh permissions" button that refetches /auth/me — it
// replays the same JWT claims and would confirm the stale state as if fresh.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, X } from 'lucide-react';
import { useAuth } from './AuthContext.jsx';
import {
  subscribePermissionsDrift,
  getPermissionsDrift,
  clearPermissionsDrift,
} from './permissionsDrift.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC, SHADOW } from '../constants/theme.js';

/**
 * Presentational notice. Rendered as a floating banner by PermissionsDriftBanner
 * and inline (`variant="inline"`) where a surface stays hidden because the
 * claims are stale.
 *
 * @param {object} props
 * @param {() => void} props.onReauthenticate the swappable action (AuthContext.reauthenticate)
 * @param {() => void} [props.onDismiss] renders a close button when supplied
 * @param {'banner'|'inline'} [props.variant]
 */
export default function PermissionsDriftNotice({ onReauthenticate, onDismiss, variant = 'banner' }) {
  const { t } = useTranslation('auth');
  const inline = variant === 'inline';

  return (
    <div
      role="alert"
      data-testid="permissions-drift-notice"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: inline ? '10px 12px' : '12px 16px',
        borderRadius: RADII.control,
        background: SEMANTIC.attenzione.bg,
        border: `1px solid ${SEMANTIC.attenzione.fg}`,
        // The attenzione pair is a badge pair: its fg is a signal colour, not a
        // text colour, so the copy stays on ink and fg carries icon + border.
        color: COLORS.ink,
        fontFamily: FONT_SANS,
        fontSize: inline ? '12px' : '13px',
        lineHeight: 1.5,
        boxShadow: inline ? 'none' : SHADOW.card,
      }}
    >
      <ShieldAlert size={16} color={SEMANTIC.attenzione.fg} style={{ flexShrink: 0, marginTop: '1px' }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{t('permissionsDrift.message')}</div>
        <div style={{ color: COLORS.textSecondary, marginTop: '2px' }}>{t('permissionsDrift.detail')}</div>
        <button
          type="button"
          onClick={onReauthenticate}
          style={{
            marginTop: '8px',
            padding: '6px 14px',
            borderRadius: RADII.control,
            border: 'none',
            background: COLORS.accent,
            color: COLORS.surface,
            fontFamily: FONT_SANS,
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {t('permissionsDrift.action')}
        </button>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('permissionsDrift.dismiss')}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: COLORS.ink,
            padding: 0,
            lineHeight: 0,
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

/**
 * Global banner: subscribes to the drift store and floats the notice over the
 * current screen. Mounted once by AuthProvider so it survives the bounce to the
 * tenant picker that a 403 TENANT_ACCESS_DENIED triggers (the picker lives
 * outside AppShell, so a shell-level banner would be unmounted exactly when it
 * is needed).
 */
export function PermissionsDriftBanner() {
  const { reauthenticate } = useAuth();
  const [drift, setDrift] = useState(getPermissionsDrift);

  useEffect(() => {
    setDrift(getPermissionsDrift());
    return subscribePermissionsDrift(setDrift);
  }, []);

  if (!drift) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '12px',
        left: '50%',
        transform: 'translateX(-50%)',
        // Above modals (1000): a session that can no longer do what it claims
        // must not be hidden behind the dialog that just failed.
        zIndex: 1100,
        maxWidth: '520px',
        width: 'calc(100% - 24px)',
      }}
    >
      <PermissionsDriftNotice
        onReauthenticate={reauthenticate}
        onDismiss={clearPermissionsDrift}
      />
    </div>
  );
}
